import type {
  AttributeFilter,
  AttributeFilterOperator,
  AttributeFilterSet,
  AttributeValue,
  FieldDescriptor,
  LayerModel,
  OperationalAttributeField,
  WorkScope,
} from '../core/types';
import { effectiveFieldCatalog, fieldValueForMasterEntity, isUserVisibleSourceField, masterSourceFieldLabel } from './attributes';
import { totalPolygonArea } from './measurement';
import { workScopeFragmentIds } from './scope';

export interface AttributeFilterCatalog {
  readonly fields: readonly FieldDescriptor[];
  readonly valuesByFieldId: ReadonlyMap<string, readonly AttributeValue[]>;
}

export interface AttributeFilterValidation {
  readonly valid: boolean;
  readonly reason?: string;
  readonly field?: FieldDescriptor;
}

export const CATEGORICAL_FILTER_OPERATORS: readonly AttributeFilterOperator[] = ['one-of', 'not-one-of'];
export const NUMERIC_FILTER_OPERATORS: readonly AttributeFilterOperator[] = ['eq', 'gt', 'gte', 'lt', 'lte', 'between'];

// Catalog per model × operational-field list (immutable, replaced on every change) × scope.
// Without the field-list key the catalog was rebuilt over every fragment on each render once an Excel join existed.
const NO_OPERATIONAL_FIELDS: readonly OperationalAttributeField[] = [];
const cache = new WeakMap<LayerModel, WeakMap<readonly OperationalAttributeField[], Map<string, AttributeFilterCatalog>>>();
let nextFilterId = 0;

const missing = (value: AttributeValue | undefined): boolean => value === null || value === undefined || typeof value === 'string' && value.trim() === '';
const normalizedValue = (value: AttributeValue | undefined): AttributeValue => missing(value) ? null : value!;
const valueKey = (value: AttributeValue): string => value === null ? 'null:' : `${typeof value}:${String(value)}`;
const scopeKey = (scope: WorkScope): string => [scope.fincaIds, scope.suerteIds, scope.tablonIds].map((ids) => [...ids].sort().join(',')).join('|');
const numericValue = (value: AttributeValue | undefined): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
};

export const attributeFilterFieldLabel = (field: FieldDescriptor): string => masterSourceFieldLabel(field.name);
export const attributeFilterValueLabel = (value: AttributeValue): string => value === null ? 'Sin valor' : typeof value === 'boolean' ? value ? 'Sí' : 'No' : String(value);

export function attributeFilterCatalog(model: LayerModel | null, scope: WorkScope, operationalFields: readonly OperationalAttributeField[] = []): AttributeFilterCatalog {
  if (!model) return { fields: [], valuesByFieldId: new Map() };
  const key = scopeKey(scope);
  const fieldsKey = operationalFields.length ? operationalFields : NO_OPERATIONAL_FIELDS;
  const cached = cache.get(model)?.get(fieldsKey)?.get(key);
  if (cached) return cached;
  const fields = effectiveFieldCatalog(model, operationalFields).filter((field) => field.source !== 'source' || isUserVisibleSourceField(field.name));
  const values = new Map(fields.map(({ id }) => [id, new Map<string, AttributeValue>()]));
  workScopeFragmentIds(model, scope).forEach((id) => {
    const fragment = model.fragmentsById.get(id);
    if (!fragment) return;
    fields.forEach((field) => {
      const value = normalizedValue(fieldValueForMasterEntity(model, field.id, [id], { fincaId: fragment.fincaId, suerteId: fragment.suerteId, tablonId: fragment.tablonId, fragmentId: fragment.id }, operationalFields));
      values.get(field.id)?.set(valueKey(value), value);
    });
  });
  const sorted = new Map<string, readonly AttributeValue[]>();
  values.forEach((items, id) => sorted.set(id, [...items.values()].sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
  })));
  const catalog = { fields, valuesByFieldId: sorted };
  const byFields = cache.get(model) ?? new WeakMap<readonly OperationalAttributeField[], Map<string, AttributeFilterCatalog>>();
  const byScope = byFields.get(fieldsKey) ?? new Map<string, AttributeFilterCatalog>();
  byScope.set(key, catalog); byFields.set(fieldsKey, byScope); cache.set(model, byFields);
  return catalog;
}

export function createAttributeFilter(field: FieldDescriptor): AttributeFilter {
  nextFilterId += 1;
  return {
    id: `attribute-filter-${Date.now()}-${nextFilterId}`,
    fieldId: field.id,
    operator: field.type === 'number' ? 'gte' : 'one-of',
    values: [],
  };
}

export function validateAttributeFilter(model: LayerModel | null, filter: AttributeFilter, operationalFields: readonly OperationalAttributeField[] = []): AttributeFilterValidation {
  const field = model ? effectiveFieldCatalog(model, operationalFields).find((candidate) => (candidate.source !== 'source' || isUserVisibleSourceField(candidate.name)) && candidate.id === filter.fieldId) : undefined;
  if (!field) return { valid: false, reason: 'El campo ya no existe en este Maestro.' };
  if (field.type === 'number') {
    if (!NUMERIC_FILTER_OPERATORS.includes(filter.operator)) return { valid: false, field, reason: 'El operador no corresponde a un campo numérico.' };
    if (!Number.isFinite(filter.value)) return { valid: false, field, reason: 'Indica un valor numérico.' };
    if (filter.operator === 'between' && !Number.isFinite(filter.upperValue)) return { valid: false, field, reason: 'Completa ambos límites del rango.' };
    if (filter.operator === 'between' && filter.upperValue! < filter.value!) return { valid: false, field, reason: 'El límite final debe ser mayor o igual al inicial.' };
    return { valid: true, field };
  }
  if (!CATEGORICAL_FILTER_OPERATORS.includes(filter.operator)) return { valid: false, field, reason: 'El operador no corresponde a un campo categórico.' };
  if (!filter.values.length) return { valid: false, field, reason: 'Selecciona al menos un valor.' };
  return { valid: true, field };
}

export const activeAttributeFilters = (model: LayerModel | null, filters: AttributeFilterSet, operationalFields: readonly OperationalAttributeField[] = []): readonly AttributeFilter[] =>
  filters.filter((filter) => validateAttributeFilter(model, filter, operationalFields).valid);

const matchesFilter = (raw: AttributeValue | undefined, filter: AttributeFilter, field: FieldDescriptor): boolean => {
  if (field.type === 'number') {
    const current = numericValue(raw);
    if (current === null || filter.value === undefined) return false;
    if (filter.operator === 'eq') return current === filter.value;
    if (filter.operator === 'gt') return current > filter.value;
    if (filter.operator === 'gte') return current >= filter.value;
    if (filter.operator === 'lt') return current < filter.value;
    if (filter.operator === 'lte') return current <= filter.value;
    return filter.upperValue !== undefined && current >= filter.value && current <= filter.upperValue;
  }
  const current = normalizedValue(raw);
  const included = filter.values.some((value) => valueKey(normalizedValue(value)) === valueKey(current));
  return filter.operator === 'not-one-of' ? !included : included;
};

export function filteredMasterFragmentIds(model: LayerModel | null, scope: WorkScope, filters: AttributeFilterSet, operationalFields: readonly OperationalAttributeField[] = []): ReadonlySet<string> {
  const scoped = workScopeFragmentIds(model, scope);
  if (!model) return scoped;
  const active = activeAttributeFilters(model, filters, operationalFields);
  if (!active.length) return scoped;
  const fields = new Map(effectiveFieldCatalog(model, operationalFields).map((field) => [field.id, field]));
  return new Set([...scoped].filter((id) => {
    const fragment = model.fragmentsById.get(id);
    return Boolean(fragment && active.every((filter) => {
      const field = fields.get(filter.fieldId);
      return Boolean(field && matchesFilter(fieldValueForMasterEntity(model, field.id, [fragment.id], { fincaId: fragment.fincaId, suerteId: fragment.suerteId, tablonId: fragment.tablonId, fragmentId: fragment.id }, operationalFields), filter, field));
    }));
  }));
}

export function attributeFilterSummary(model: LayerModel | null, ids: ReadonlySet<string>): { readonly fragmentCount: number; readonly suerteCount: number; readonly areaHa: number } {
  if (!model) return { fragmentCount: 0, suerteCount: 0, areaHa: 0 };
  const fragments = [...ids].flatMap((id) => {
    const fragment = model.fragmentsById.get(id);
    return fragment ? [fragment] : [];
  });
  const geometries = fragments.flatMap(({ feature }) => {
    const geometry = feature.getGeometry();
    return geometry?.getType().includes('Polygon') ? [geometry] : [];
  });
  return {
    fragmentCount: fragments.length,
    suerteCount: new Set(fragments.map(({ suerteId }) => suerteId)).size,
    areaHa: geometries.length ? totalPolygonArea(geometries) / 10_000 : 0,
  };
}

const isOperator = (value: unknown): value is AttributeFilterOperator =>
  [...CATEGORICAL_FILTER_OPERATORS, ...NUMERIC_FILTER_OPERATORS].includes(value as AttributeFilterOperator);
const finite = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? value : undefined;
const filterValue = (value: unknown): value is AttributeValue => value === null || typeof value === 'string' || typeof value === 'number' && Number.isFinite(value) || typeof value === 'boolean';

/** Accepts current filters and the previous single-filter shape persisted by early v1 projects. */
export function normalizeAttributeFilterSet(value: unknown): AttributeFilterSet {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => {
    if (!item || typeof item !== 'object') return [];
    const candidate = item as Record<string, unknown>;
    const fieldId = typeof candidate.fieldId === 'string' ? candidate.fieldId : '';
    if (!fieldId) return [];
    const currentOperator = isOperator(candidate.operator) ? candidate.operator : undefined;
    const legacy = currentOperator === undefined;
    const legacyMin = finite(candidate.min); const legacyMax = finite(candidate.max);
    const operator = currentOperator
      ? currentOperator
      : legacyMin !== undefined && legacyMax !== undefined ? 'between'
        : legacyMin !== undefined ? 'gte'
          : legacyMax !== undefined ? 'lte'
            : 'one-of';
    const values = Array.isArray(candidate.values) ? candidate.values.filter(filterValue).map((entry) => legacy && entry === 'Sin valor' ? null : normalizedValue(entry)) : [];
    const valueNumber = finite(candidate.value) ?? legacyMin ?? legacyMax;
    const upperValue = finite(candidate.upperValue) ?? (operator === 'between' ? legacyMax : undefined);
    return [{
      id: typeof candidate.id === 'string' && candidate.id ? candidate.id : `attribute-filter-${index + 1}`,
      fieldId,
      operator,
      values,
      ...(valueNumber !== undefined ? { value: valueNumber } : {}),
      ...(upperValue !== undefined ? { upperValue } : {}),
    }];
  });
}
